import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiExternalLink, FiLock, FiSearch } from 'react-icons/fi';
import './ToolsHub.css';

// Tool definitions - centralized registry
const TOOLS = {
  portfolio: [
    {
      id: 'ad-tech',
      name: 'Ad Tech Navigator',
      icon: '🗺️',
      description: 'Interactive map of the ad tech ecosystem. Filter by business type to see relevant platforms, market share, and how each layer connects.',
      category: 'Analytics',
      status: 'active',
      route: '/tools/ad-tech',
      isNew: true,
    },
    {
      id: 'series-a-tracker',
      name: 'Series A Tracker',
      icon: '🚀',
      description: 'Investor pipeline management for Series A raises. Track meetings, momentum, and term sheet comparisons with real-time dashboards.',
      category: 'Fundraising',
      status: 'coming_soon',
      isNew: true,
    },
    {
      id: 'runway-calculator',
      name: 'Runway Calculator',
      icon: '📊',
      description: 'Calculate cash runway, model burn scenarios, and project zero-cash dates. Includes multi-scenario ROAS modeling with Bear/Base/Bull projections.',
      category: 'Operations',
      status: 'coming_soon',
    },
    {
      id: 'ua-playbooks',
      name: 'UA Playbooks',
      icon: '📚',
      description: 'Interactive UA strategy playbooks for portfolio companies. Phase-based goal progression, channel assessment, anti-patterns, and recovery planning.',
      category: 'Analytics',
      status: 'coming_soon',
    },
    {
      id: 'roas-analytics',
      name: 'ROAS Analytics',
      icon: '📈',
      description: 'Cohort analysis, D7→D365 projections, and retention/monetization gap analysis for portfolio companies. Paste data in any format.',
      category: 'Analytics',
      status: 'coming_soon',
    },
    {
      id: 'kpi-measurement',
      name: 'KPI Measurement',
      icon: '📊',
      description: 'Statistical calculators for version-based A/B testing. Sample size, test duration, spend optimization, and Two-Test Protocol for ROAS.',
      category: 'Analytics',
      status: 'coming_soon',
      isNew: true,
    },
    {
      id: 'email-analytics',
      name: 'Email Analytics',
      icon: '📧',
      description: 'Track email opens and clicks for all emails sent via gmail_send.py. View campaign performance, open rates, and click-through metrics.',
      category: 'Operations',
      status: 'coming_soon',
      isNew: true,
    },
    {
      id: 'metrics-dashboard',
      name: 'Metrics Dashboard',
      icon: '🎯',
      description: 'Self-service metrics upload and analysis. Upload retention/ROAS data, get instant projections and benchmarks against gaming industry standards.',
      category: 'Analytics',
      status: 'coming_soon',
      isNew: true,
    },
  ],
  fund: [
    {
      id: 'deal-flow',
      name: 'Deal Flow Pipeline',
      icon: '💼',
      description: 'Pipeline management for prospective investments. Track deal stages, investment committee decisions, and due diligence progress.',
      category: 'Deal Flow',
      status: 'coming_soon',
    },
    {
      id: 'deal-scoring',
      name: 'Deal Scoring',
      icon: '⚖️',
      description: 'Standardized investment scoring framework. Team pedigree assessment, market sizing, ROAS data validation, and return modeling.',
      category: 'Deal Flow',
      status: 'coming_soon',
      isNew: true,
    },
    {
      id: 'lp-reporting',
      name: 'LP Reporting',
      icon: '📋',
      description: 'Quarterly LP report generation with portfolio metrics, valuations, and fund performance summaries. Export to PDF.',
      category: 'LP Relations',
      status: 'coming_soon',
    },
    {
      id: 'portfolio-dashboard',
      name: 'Portfolio Dashboard',
      icon: '🏢',
      description: 'Cross-portfolio view of all active investments. Aggregate metrics, runway status, key milestones, and follow-on tracking.',
      category: 'LP Relations',
      status: 'coming_soon',
      isNew: true,
    },
    {
      id: 'user-management',
      name: 'User Management',
      icon: '👥',
      description: 'Manage whitelisted users and admin access. View access logs and activity tracking across all tools.',
      category: 'Admin',
      status: 'coming_soon',
    },
  ],
};

const CATEGORIES = {
  portfolio: ['All', 'Fundraising', 'Operations', 'Analytics'],
  fund: ['All', 'Deal Flow', 'LP Relations', 'Admin'],
};

function ToolsHub() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('portfolio');
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get first name from user
  const getFirstName = () => {
    if (!user?.name) return '';
    return user.name.split(' ')[0];
  };

  // Filter tools based on category and search
  const getFilteredTools = (tools) => {
    return tools.filter(tool => {
      const matchesCategory = activeFilter === 'All' || tool.category === activeFilter;
      const matchesSearch = !searchQuery ||
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  };

  const renderToolCard = (tool) => {
    const isActive = tool.status === 'active';
    const CardWrapper = isActive ? Link : 'div';
    const cardProps = isActive ? { to: tool.route } : {};

    return (
      <CardWrapper
        key={tool.id}
        className={`tool-card ${!isActive ? 'disabled' : ''}`}
        {...cardProps}
      >
        <div className="tool-card-header">
          <span className="tool-icon">{tool.icon}</span>
          {!isActive && <span className="coming-soon-badge">Coming Soon</span>}
        </div>

        <div className="tool-card-content">
          <h3 className="tool-name">
            {tool.name}
            {tool.isNew && <span className="new-badge">New</span>}
          </h3>
          <p className="tool-description">{tool.description}</p>
        </div>

        <div className="tool-card-footer">
          <span className={`category-tag ${tool.category.toLowerCase().replace(' ', '-')}`}>
            {tool.category}
          </span>
          {isActive ? (
            <FiExternalLink className="tool-link-icon" />
          ) : (
            <FiLock className="tool-lock-icon" />
          )}
        </div>
      </CardWrapper>
    );
  };

  return (
    <div className="tools-hub">
      {/* Header Section */}
      <header className="tools-header">
        <div className="tools-header-content">
          <h1 className="tools-greeting">
            {getGreeting()}, <span className="user-name">{getFirstName()}</span>
          </h1>
          <p className="tools-subtitle">
            Dashboards and analytics for Transcend and portfolio companies.
          </p>
        </div>

        {/* Search Bar */}
        <div className="tools-search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            className="tools-search"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </header>

      {/* Section Tabs */}
      <div className="tools-section-tabs">
        <button
          className={`section-tab ${activeSection === 'portfolio' ? 'active' : ''}`}
          onClick={() => { setActiveSection('portfolio'); setActiveFilter('All'); }}
        >
          Portfolio Company Tools
        </button>
        <button
          className={`section-tab ${activeSection === 'fund' ? 'active' : ''}`}
          onClick={() => { setActiveSection('fund'); setActiveFilter('All'); }}
        >
          Fund Management
        </button>
      </div>

      {/* Category Filters */}
      <div className="tools-filters">
        {CATEGORIES[activeSection].map(category => (
          <button
            key={category}
            className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
            onClick={() => setActiveFilter(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Tools Grid */}
      <div className="tools-grid">
        {getFilteredTools(TOOLS[activeSection]).map(renderToolCard)}
      </div>

      {/* Footer */}
      <footer className="tools-footer">
        <p className="footer-text">Private and Confidential</p>
        <p className="footer-copyright">© 2025 Transcend. All Rights Reserved.</p>
        <p className="footer-contact">Questions? Contact andrew@transcend.fund</p>
      </footer>
    </div>
  );
}

export default ToolsHub;
